import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../../lib/supabase'
import type { ChatTab } from '../../../hooks/useTabManager'
import * as Sentry from '@sentry/react'
import type { AgentSearchResult } from './useAgentSearch'

export const useAgentAvatars = (tabs: ChatTab[], searchResults: AgentSearchResult[]) => {
    const [resolvedAvatars, setResolvedAvatars] = useState<Record<string, string | null>>({})

    // Load missing avatars for tabs
    useEffect(() => {
        async function loadMissingAvatars() {
            if (!tabs) return

            // Identify agents whose avatars are missing from cache
            const missingAgentIds = tabs
                .map(t => t.agent_id)
                .filter(id => id && resolvedAvatars[id] === undefined)

            // Deduplicate
            const uniqueMissingIds = [...new Set(missingAgentIds)]

            if (uniqueMissingIds.length === 0) return

            try {
                const { data } = await supabase
                    .from('agents')
                    .select('id, avatar_url')
                    .in('id', uniqueMissingIds as string[])

                if (data) {
                    const newAvatars: Record<string, string | null> = {}
                    data.forEach(agent => {
                        newAvatars[agent.id] = agent.avatar_url
                    })

                    // Also mark missing ones as null to avoid refetching
                    uniqueMissingIds.forEach(id => {
                        if (id && !(id in newAvatars)) {
                            newAvatars[id] = null
                        }
                    })

                    setResolvedAvatars(prev => ({
                        ...prev,
                        ...newAvatars
                    }))
                }
            } catch (error) {
                Sentry.captureException(error, {
                    tags: { hook: 'useAgentAvatars', action: 'loadTabAvatars' },
                    extra: { tabIds: tabs.map(t => t.id) }
                })
            }
        }

        loadMissingAvatars()
    }, [tabs, resolvedAvatars])

    // Combine resolved avatars with search results
    const agentAvatars = useMemo(() => {
        // Base map from search results
        const map = searchResults.reduce((acc, agent) => {
            acc[agent.id] = agent.avatar_url || null
            return acc
        }, {} as Record<string, string | null>)

        // Merge with resolved avatars (cache takes precedence or fills gaps)
        return { ...map, ...resolvedAvatars }
    }, [searchResults, resolvedAvatars])

    return agentAvatars
}
